import { Link, useNavigate } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import AppShell from "@/components/AppShell";
import { KETE, PROGRESS_LINES } from "@/lib/content";
import { formatDate, startPlanPath } from "@/lib/api";
import { useMe } from "@/context/MeContext";

const Count = ({ n, of, label, note, testId }) => (
  <div className="ahu-card p-5" data-testid={testId}>
    <p className="font-display text-4xl text-charcoal">{n}{of !== undefined && <span className="text-2xl text-ink-soft"> of {of}</span>}</p>
    <p className="mt-1 text-sm text-ink-soft">{label}</p>
    {note && <p className="mt-2 text-sm text-charcoal" data-testid={`${testId}-note`}>{note}</p>}
  </div>
);

export default function HomePage() {
  const { me } = useMe();
  const nav = useNavigate();
  const s = me.stats;
  const name = me.profile?.preferred_name || me.profile?.first_name;
  const line = PROGRESS_LINES[(s.plans_total || 0) % PROGRESS_LINES.length];
  const actionsTotal = s.plans_total * 3;
  const actionsLeft = actionsTotal - s.actions_completed;
  const accLeft = s.plans_total - s.accountabilities_completed;
  const atLimit = (me.is_member || me.role === "admin") && s.active_plans >= 3;
  const testDone = me.tester && !me.plans_open && me.role !== "admin" && me.plans_created >= 1;

  return (
    <AppShell>
      <div className="fade-in" data-testid="home-page">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl" data-testid="home-greeting">Kia ora {name},</h1>
        <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-ink" data-testid="home-portal-intro">
          Welcome to your members portal, a place to return to between plans. This is your own private space. Only you can see what's here: your Action Plans to review and work through, and Ako, your learning resources.
        </p>

        {me.migrated && (me.bonus_plans > 0 || me.is_member) && (
          <div className="ahu-card mt-8 p-5 border-l-4 !border-l-gold" data-testid="welcome-back-card">
            <p className="font-display text-lg text-charcoal">Welcome to the new AHU</p>
            <p className="mt-1 text-[15px] text-ink-soft">Your plans from the first AHU app are here. Thank you for helping us test it.{!me.is_member && " Your next Action Plan is on us."}</p>
          </div>
        )}

        {s.next_checkin && (
          <Link to={`/plans/${s.next_checkin.plan_id}`} className="ahu-card mt-8 p-5 flex items-center gap-4 hover:border-charcoal/40" style={{ transition: "border-color .3s ease" }} data-testid="next-checkin-card">
            <CalendarDays className="text-gold shrink-0" size={22} />
            <div>
              <p className="text-sm text-ink-soft">Next check-in</p>
              <p className="font-medium text-charcoal">{formatDate(s.next_checkin.date, { weekday: "long", day: "numeric", month: "long" })} · {s.next_checkin.title}</p>
            </div>
          </Link>
        )}

        <section className="mt-10">
          <h2 className="text-xl sm:text-2xl mb-4">Your progress</h2>
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
            <Count n={s.actions_completed} of={actionsTotal} label="Actions completed" testId="stat-actions"
              note={s.plans_total ? (actionsLeft ? `${actionsLeft} still to do` : "All done") : null} />
            <Count n={s.accountabilities_completed} of={s.plans_total} label="Accountabilities completed" testId="stat-accountabilities"
              note={s.plans_total ? (accLeft ? `${accLeft} still to do` : "All done") : null} />
            <Count n={s.plans_total} label="Plans in total" testId="stat-plans" />
          </div>
          <p className="mt-4 font-display text-lg text-charcoal" data-testid="progress-line">{line}</p>
        </section>

        <section className="mt-10">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-xl sm:text-2xl">My three baskets</h2>
            <Link to="/baskets" className="btn-quiet text-sm" data-testid="change-baskets-link">Change</Link>
          </div>
          {me.baskets ? (
            <div className="grid gap-3 sm:grid-cols-3" data-testid="home-baskets">
              {KETE.map((k) => (
                <div key={k.key} className="ahu-card p-5">
                  <p className="font-display text-charcoal">{k.name}</p>
                  <p className="mt-1 text-sm text-ink-soft">{me.baskets[k.key]}</p>
                </div>
              ))}
            </div>
          ) : (
            <Link to="/baskets" className="ahu-card p-5 block text-ink-soft hover:border-charcoal/40" data-testid="choose-baskets-card">Choose your three focus areas, one from each kete.</Link>
          )}
        </section>

        <div className="mt-12">
          {atLimit && (
            <div className="ahu-card p-5 mb-5 border-l-4 !border-l-sage" data-testid="active-limit-message">
              <p className="font-display text-lg text-charcoal">You have 3 active plans</p>
              <p className="mt-1 text-[15px] text-ink-soft">Complete one of your Action Plans before starting a new one. One plan done well matters most.</p>
              <Link to="/plans" className="btn-quiet text-sm mt-3 inline-block" data-testid="active-limit-plans-link">Go to my plans</Link>
            </div>
          )}
          {testDone && (
            <div className="ahu-card p-5 mb-5 border-l-4 !border-l-sage" data-testid="tester-done-message">
              <p className="font-display text-lg text-charcoal">Thank you for testing AHU</p>
              <p className="mt-1 text-[15px] text-ink-soft">You've made your Action Plan, and it's yours to keep and work through. Starting new plans opens when AHU launches, and we'll let you know.</p>
            </div>
          )}
          <button className={`btn-gold w-full sm:w-auto sm:min-w-[240px] ${atLimit || testDone ? "!bg-rule !text-ink-soft !border-rule cursor-not-allowed" : ""}`}
            disabled={atLimit || testDone} aria-disabled={atLimit || testDone} onClick={() => nav(startPlanPath(me))} data-testid="start-new-plan-btn">Start a new plan</button>
        </div>
      </div>
    </AppShell>
  );
}
